import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useState, useCallback } from "react";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";
const API = apiUrl("");

/**
 * Estate inspection — a snapshot of the whole organisation.
 *
 * Everything on this page is computed server-side and shown with the figures
 * behind it. The narrative is written from the same digest, so any sentence in
 * it can be checked against the tables below — and the digest itself is one
 * click away. That matters more than it sounds: this page invites judgements
 * about branches, so a reader has to be able to see where a claim came from.
 *
 * Nothing here is a verdict. An outlier is a branch worth a look.
 */
const EstateInspection = () => {
  const masked = useMaskNames();
  const [snap, setSnap] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState(6);
  const [forwardMonths, setForwardMonths] = useState(12);
  const [showDigest, setShowDigest] = useState(false);
  const [domainFilter, setDomainFilter] = useState("");
  const token = localStorage.getItem("authToken");
  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/estateInspection/snapshot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          months,
          forwardMonths,
          narrative: true
        })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      if (d.empty) {
        setError("No branches visible for this organisation.");
        setSnap(null);
      } else {
        setSnap(d.snapshot);
        setNarrative(d.narrative);
      }
    } catch (e) {
      setError(`Could not run the inspection: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token, months, forwardMonths]);
  const fmt = (v, unit) => {
    if (v === null || v === undefined) return "—";
    return unit === "%" ? `${v}%` : `${v}`;
  };
  const sevColour = s => s === "red" ? "#CF1322" : "#a05a00";
  const sevBg = s => s === "red" ? "#FFF1F0" : "#fff4e5";

  // A branch appearing across several domains says more than any single
  // reading, so the list is grouped by branch rather than by metric.
  const byBranch = new Map();
  for (const o of snap?.outliers || []) {
    if (domainFilter && o.domain !== domainFilter) continue;
    if (!byBranch.has(o.branch)) byBranch.set(o.branch, []);
    byBranch.get(o.branch).push(o);
  }
  const grouped = [...byBranch.entries()].sort((a, b) => {
    const reds = l => l.filter(x => x.severity === "red").length;
    return reds(b[1]) - reds(a[1]) || b[1].length - a[1].length;
  });
  const domains = [...new Set((snap?.metricDefs || []).map(d => d.domain))];
  const card = {
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    background: "#fff",
    marginBottom: 16
  };
  const cardHead = {
    padding: "9px 12px",
    background: "#FAFAFA",
    borderBottom: "1px solid #F0F0F0",
    fontWeight: "bold"
  };
  const th = {
    textAlign: "left",
    padding: "6px 8px",
    fontSize: "0.82em",
    color: "#5D5D5D",
    whiteSpace: "nowrap"
  };
  const td = {
    padding: "6px 8px",
    fontSize: "0.85em"
  };
  return <div style={{
    padding: 16
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }}>
        <h2 style={{
        margin: 0
      }}>Estate inspection</h2>
        <MaskToggleButton />
      </div>

      <p style={{
      fontSize: "0.9em",
      color: "#555",
      marginTop: 6,
      maxWidth: 960
    }}>
        Every branch measured against its peers across activity, deliveries,
        staffing, attendance, safety, SOPs, services and messaging. A branch is
        flagged when it sits well away from the rest &mdash; which is a reason to
        go and look, not a conclusion about the people who work there.
      </p>

      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "14px 0"
    }}>
        <label style={{
        fontSize: "0.9em"
      }}>
          Look back
          <select value={months} onChange={e => setMonths(+e.target.value)} style={{
          padding: 5,
          marginLeft: 5
        }}>
            {[3, 6, 9, 12].map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
        </label>
        <label style={{
        fontSize: "0.9em"
      }}>
          Look ahead
          <select value={forwardMonths} onChange={e => setForwardMonths(+e.target.value)} style={{
          padding: 5,
          marginLeft: 5
        }}>
            {[3, 6, 12].map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
        </label>
        <button onClick={run} disabled={loading} style={{
        background: "#0958D9",
        color: "#fff",
        border: 0,
        borderRadius: 4,
        padding: "7px 16px",
        cursor: loading ? "default" : "pointer"
      }}>
          {loading ? "Inspecting…" : snap ? "Run again" : "Run inspection"}
        </button>
        {snap && <span style={{
        fontSize: "0.82em",
        color: "#5D5D5D"
      }}>
            {snap.branchCount} branches · generated{" "}
            {new Date(snap.generatedAt).toLocaleString("en-GB")}
          </span>}
      </div>

      {loading && <div style={{
      padding: 14,
      color: "#5D5D5D"
    }}>
          Walking every branch. This reads a lot of data and can take a minute.
        </div>}

      {error && <div style={{
      background: "#fff4f4",
      border: "1px solid #e0b4b4",
      padding: "9px 12px",
      marginBottom: 12
    }}>{error}</div>}

      {!snap && !loading && !error && <div style={{
      ...card,
      padding: 20,
      color: "#5D5D5D"
    }}>
          Nothing has been run yet. The inspection reads live data across the
          whole organisation, so it is deliberately on demand rather than
          refreshing behind you.
        </div>}

      {snap && <>
          {!snap.comparable && <div style={{
        background: "#fff4e5",
        border: "1px solid #e0c088",
        padding: "9px 12px",
        marginBottom: 16
      }}>
              <strong>Too few branches to rank.</strong> With fewer than{" "}
              {snap.config.minBranchesToCompare} comparable branches there is no
              meaningful peer group, so no outliers are reported. The figures
              below are still shown.
            </div>}

          {/* ---------------------------------------------------- narrative */}
          {narrative && <div style={card}>
              <div style={cardHead}>
                Summary
                <span style={{
            fontWeight: "normal",
            color: "#5D5D5D",
            fontSize: "0.82em"
          }}>
                  {"  "}written by {narrative.source}
                  {narrative.modelError && " (model unavailable)"}
                </span>
              </div>
              <div style={{
          padding: 14,
          whiteSpace: "pre-wrap",
          lineHeight: 1.55,
          fontSize: "0.95em"
        }}>
                {narrative.text}
              </div>
              {snap.digest && <div style={{
          padding: "0 14px 12px"
        }}>
                  <button onClick={() => setShowDigest(!showDigest)} style={{
            background: "#fff",
            color: "#0958D9",
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            padding: "4px 10px",
            fontSize: "0.85em"
          }}>
                    {showDigest ? "Hide" : "Show"} the figures this was written from
                  </button>
                  {showDigest && <pre style={{
            marginTop: 10,
            padding: 10,
            background: "#FAFAFA",
            border: "1px solid #F0F0F0",
            fontSize: "0.78em",
            whiteSpace: "pre-wrap",
            maxHeight: 340,
            overflowY: "auto"
          }}>{snap.digest}</pre>}
                </div>}
            </div>}

          {/* ----------------------------------------------------- outliers */}
          <div style={card}>
            <div style={{
          ...cardHead,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <span>Branches standing apart ({snap.outliers.length})</span>
              <select value={domainFilter} onChange={e => setDomainFilter(e.target.value)} style={{
            padding: 4,
            fontWeight: "normal",
            fontSize: "0.85em"
          }}>
                <option value="">All domains</option>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {grouped.length === 0 ? <div style={{
          padding: 14,
          color: "#5D5D5D"
        }}>
                No branch sits away from its peers on any measured domain
                {domainFilter && ` in ${domainFilter}`}.
              </div> : grouped.map(([branch, list]) => <div key={branch} style={{
          borderBottom: "1px solid #F5F5F5",
          padding: "10px 12px"
        }}>
                <div style={{
            fontWeight: "bold",
            marginBottom: 5
          }}>
                  {displayName(branch, masked)}
                  <span style={{
              fontWeight: "normal",
              color: "#5D5D5D",
              fontSize: "0.85em"
            }}>
                    {"  "}{list.length} measure{list.length === 1 ? "" : "s"} across{" "}
                    {[...new Set(list.map(o => o.domain))].join(", ")}
                  </span>
                </div>
                {list.map((o, i) => <div key={i} style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            padding: "3px 0",
            fontSize: "0.88em"
          }}>
                    <span style={{
              background: sevBg(o.severity),
              color: sevColour(o.severity),
              borderRadius: 3,
              padding: "0 6px",
              fontSize: "0.82em",
              minWidth: 44,
              textAlign: "center"
            }}>
                      {o.severity}
                    </span>
                    <span style={{
              minWidth: 74,
              color: "#5D5D5D",
              fontSize: "0.9em"
            }}>
                      {o.domain}
                    </span>
                    <span style={{
              flex: 1
            }}>
                      {o.label}{" "}
                      <strong style={{
                color: sevColour(o.severity)
              }}>
                        {fmt(o.value, o.unit)}
                      </strong>
                      <span style={{
                color: "#5D5D5D"
              }}>
                        {" "}against a peer median of {fmt(o.median, o.unit)}
                        {" "}· {o.peers} branches compared
                      </span>
                    </span>
                  </div>)}
              </div>)}
          </div>

          {/* ------------------------------------------------ forward holes */}
          <div style={card}>
            <div style={cardHead}>
              Staffing pressure in the next {snap.forwardMonths} months
              ({snap.staffingHoles.length})
            </div>
            {snap.staffingHoles.length === 0 ? <div style={{
          padding: 14,
          color: "#5D5D5D"
        }}>
                No branch has an unusual amount of approved leave booked ahead.
              </div> : <table style={{
          width: "100%",
          borderCollapse: "collapse"
        }}>
                <thead><tr style={{
              background: "#fafafa"
            }}>
                  <th style={th}>Branch</th><th style={th}>Month</th>
                  <th style={{
                ...th,
                textAlign: "right"
              }}>Staff-days booked</th>
                  <th style={{
                ...th,
                textAlign: "right"
              }}>Of capacity</th>
                </tr></thead>
                <tbody>
                  {snap.staffingHoles.map((h, i) => <tr key={i} style={{
              borderTop: "1px solid #F5F5F5",
              background: sevBg(h.severity)
            }}>
                      <td style={td}>{displayName(h.branch, masked)}</td>
                      <td style={td}>{h.month}</td>
                      <td style={{
                ...td,
                textAlign: "right"
              }}>
                        {h.staffDays} <span style={{
                  color: "#5D5D5D"
                }}>({h.staff} staff)</span>
                      </td>
                      <td style={{
                ...td,
                textAlign: "right",
                fontWeight: "bold",
                color: sevColour(h.severity)
              }}>{h.lostPct}%</td>
                    </tr>)}
                </tbody>
              </table>}
          </div>

          {/* ------------------------------------------------ full readings */}
          <div style={card}>
            <div style={cardHead}>
              Every branch, every measure
              <span style={{
            fontWeight: "normal",
            color: "#5D5D5D",
            fontSize: "0.82em"
          }}>
                {"  "}a blank means there was too little to judge on
              </span>
            </div>
            <div style={{
          overflowX: "auto"
        }}>
              <table style={{
            borderCollapse: "collapse",
            minWidth: "100%"
          }}>
                <thead>
                  <tr style={{
                background: "#fafafa"
              }}>
                    <th style={{
                  ...th,
                  position: "sticky",
                  left: 0,
                  background: "#fafafa"
                }}>
                      Branch
                    </th>
                    {snap.metricDefs.map(d => <th key={d.key} style={{
                  ...th,
                  textAlign: "right"
                }} title={`${d.domain} — ${d.worse === "high" ? "higher is worse" : "lower is worse"}`}>
                        {d.label}
                      </th>)}
                  </tr>
                  <tr style={{
                background: "#FAFAFA"
              }}>
                    <td style={{
                  ...td,
                  fontWeight: "bold",
                  position: "sticky",
                  left: 0,
                  background: "#FAFAFA"
                }}>Estate median</td>
                    {snap.metricDefs.map(d => <td key={d.key} style={{
                  ...td,
                  textAlign: "right",
                  fontWeight: "bold"
                }}>
                        {snap.estate[d.key]?.median ?? "—"}
                      </td>)}
                  </tr>
                </thead>
                <tbody>
                  {snap.branches.map(b => <tr key={b.dbName} style={{
                borderTop: "1px solid #F5F5F5"
              }}>
                      <td style={{
                  ...td,
                  whiteSpace: "nowrap",
                  position: "sticky",
                  left: 0,
                  background: "#fff"
                }}>
                        {displayName(b.name, masked)}
                        {b.error && <div style={{
                    color: "#CF1322",
                    fontSize: "0.85em"
                  }}>
                            could not be read
                          </div>}
                        {!b.error && <div style={{
                    color: "#8C8C8C",
                    fontSize: "0.82em"
                  }}>
                            {b.raw?.staff ?? "—"} staff · {b.raw?.items ?? 0} items
                          </div>}
                      </td>
                      {snap.metricDefs.map(d => {
                  const v = b.metrics?.[d.key];
                  const flagged = (snap.outliers || []).find(o => o.dbName === b.dbName && o.metric === d.key);
                  return <td key={d.key} style={{
                    ...td,
                    textAlign: "right",
                    background: flagged ? sevBg(flagged.severity) : undefined,
                    color: flagged ? sevColour(flagged.severity) : "#262626",
                    fontWeight: flagged ? "bold" : "normal"
                  }}>
                            {fmt(v, d.unit)}
                          </td>;
                })}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div style={{
          padding: "8px 12px",
          fontSize: "0.8em",
          color: "#5D5D5D"
        }}>
              Branches are compared using the median and median absolute deviation
              rather than an average, so one very large or very unusual branch
              cannot mask the rest. Item counts come from the Periodic Task
              recorded by each branch &mdash; a branch that stops recording them
              drops out of the per-item comparisons rather than appearing to shrink.
            </div>
          </div>
        </>}
    </div>;
};
export default EstateInspection;
